import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import {
  ChangeWorkOrderItemDiscountRequest,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import formatterPrice from '@/utils/number/formatterPrice';
import {
  Checkbox,
  Col,
  Divider,
  Form,
  FormInstance,
  Modal,
  ModalProps,
  Row,
  Typography,
} from 'antd';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useEffect } from 'react';

const { Text } = Typography;

type Props = ModalProps & {
  itemType?: WorkOrderItemType;
  form: FormInstance<ChangeWorkOrderItemDiscountRequest>;
  maxDiscount: number;
  errorMessage?: string | null;
};

const WoekOrderItemsDiscountModal: React.FC<Props> = ({
  itemType,
  form,
  errorMessage,
  ...props
}) => {
  const isPart = itemType === WorkOrderItemType.Part;
  const requiredField = useDefaultEmptyFieldError();
  const priceDiscount = Form.useWatch('priceDiscount', form);

  const {
    workorder: { data },
  } = useWorkorderDetailContext();

  const hasDiscountAboveDefault = hasPermissions([
    Authorities.MOVIMENTATION_WORKORDER_GIVE_DISCOUNT_OVER_LIMIT,
  ]);

  const hasDiscountAboveCompany = hasPermissions([
    Authorities.MOVIMENTATION_WORKORDER_ADD_DISCOUNT_PERCENTAGE_GREATER_THAN_DEFINED_BY_THE_COMPANY,
  ]);

  const workorderConfig = useWorkorderConfigurationCompanyQuery(
    data?.company.id,
  );

  const maxDiscountPercentage = isPart
    ? workorderConfig.data?.maxPartDiscount || 0
    : workorderConfig.data?.maxServiceDiscount || 0;

  const maxDiscount =
    !hasDiscountAboveDefault && !hasDiscountAboveCompany
      ? props.maxDiscount * maxDiscountPercentage
      : props.maxDiscount;

  useEffect(() => {
    if (!!priceDiscount) {
      form.validateFields(['priceDiscount']);
    }
  }, [workorderConfig.dataUpdatedAt]);

  return (
    <Modal
      title={
        <Group gap={10}>
          <FaExclamationTriangle color="#f33e3e" size={24} /> Atenção
        </Group>
      }
      okText="Salvar"
      destroyOnClose
      {...props}
    >
      <Form preserve={false} form={form}>
        <Text>
          Ao alterar o valor total de desconto em {isPart ? 'peça' : 'serviço'},
          o valor do desconto em cada {isPart ? 'peça' : 'serviço'} será
          alterado de forma proporcional ao valor total recém alterado e ao
          valor total {isPart ? 'da peça' : 'do serviço'}!
        </Text>

        <Divider />

        <Row gutter={[16, 8]}>
          <Col span={24} md={12}>
            <FloatingFormItem
              name="priceDiscount"
              label="Desconto(R$)"
              rules={[
                requiredField,
                {
                  max: maxDiscount,
                  type: 'number',
                  message: `Limite máximo ${formatterPrice(maxDiscount)}`,
                },
              ]}
            >
              <InputDecimal
                fractions={2}
                max={props.maxDiscount}
                min={0}
                disabled={workorderConfig.isLoading}
              />
            </FloatingFormItem>
          </Col>

          {isPart && (
            <Col span={24} md={12}>
              <FloatingFormItem name="isProvided" valuePropName="checked">
                <Checkbox>
                  Distribuir desconto entre as peças marcadas como "Fornecidas"
                </Checkbox>
              </FloatingFormItem>
            </Col>
          )}
        </Row>

        {!!errorMessage && (
          <strong style={{ color: 'red' }}>{errorMessage}</strong>
        )}
      </Form>
    </Modal>
  );
};

export default WoekOrderItemsDiscountModal;
